// src/components/AdminDashboard/sections/AddCourtSection.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Typography, TextField, Button, List, ListItem, ListItemText, IconButton, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { FiX } from 'react-icons/fi';



const AddCourtSection = ({
  newCourtName,
  setNewCourtName,
  newCourtHlsUrl,
  setNewCourtHlsUrl,
  ownerUsername,
  setOwnerUsername,
  newCourtCameraName,
  setNewCourtCameraName,
  newCourtCameraUrl,
  setNewCourtCameraUrl,
  sessions,
  tempSessionName,
  setTempSessionName,
  tempDayOfWeek,
  setTempDayOfWeek,
  tempStartTime,
  setTempStartTime,
  tempEndTime,
  setTempEndTime,
  tempActive,
  setTempActive,
  handleAddSession,
  handleRemoveSession,
  handleAddCourt,
  cameraUrls = {},
  handleAddCamera,
  handleRemoveCamera
}) => (
  <div className="bg-gradient-to-br from-white to-gray-50/50 rounded-2xl p-8 mb-8 shadow-lg border border-gray-100 hover:shadow-xl hover:border-[#00a693]/20 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-1 before:bg-gradient-to-r before:from-[#00a693] before:to-[#ff6f3c] before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300">
    {/* Section Header */}
    <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-[#00a693]/10">
      <div>
        <Typography variant="h6" className="text-[#5a7684] font-bold text-xl mb-2">
          Add Court
        </Typography>
        <Typography variant="body2" className="text-[#666666] text-sm">
          Create a new court and assign it to an owner.
        </Typography>
      </div>
    </div>

    {/* Form */}
    <form onSubmit={handleAddCourt} className="flex flex-col gap-6 max-w-4xl p-6 bg-gradient-to-br from-[#00a693]/5 to-[#ff6f3c]/5 rounded-xl border border-[#00a693]/10">
      {/* Basic Court Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TextField
          label="Court Name"
          value={newCourtName}
          onChange={(e) => setNewCourtName(e.target.value)}
          required
          className="[&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-notchedOutline]:border-gray-300 hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
        />
        <TextField
          label="HLS URL"
          value={newCourtHlsUrl}
          onChange={(e) => setNewCourtHlsUrl(e.target.value)}
          className="[&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-notchedOutline]:border-gray-300 hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
        />
      </div>
      
      <TextField
        label="Owner Username"
        value={ownerUsername}
        onChange={(e) => setOwnerUsername(e.target.value)}
        required
        placeholder="Enter the username of the court owner"
        className="[&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-notchedOutline]:border-gray-300 hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
      />

      {/* Camera Section */}
      <div className="mt-6">
        <Typography variant="subtitle1" className="text-[#5a7684] font-semibold mb-4">Add Cameras</Typography>
        <div className="flex flex-wrap gap-3 items-center">
          <TextField
            label="Camera Name"
            value={newCourtCameraName}
            onChange={(e) => setNewCourtCameraName(e.target.value)}
            placeholder="e.g., Main Camera"
            className="flex-1 min-w-[200px] [&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-notchedOutline]:border-gray-300 hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
          />
          <TextField
            label="Camera RTSP URL"
            value={newCourtCameraUrl}
            onChange={(e) => setNewCourtCameraUrl(e.target.value)}
            placeholder="rtsp://..."
            className="flex-1 min-w-[300px] [&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-notchedOutline]:border-gray-300 hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
          />
          <Button 
            variant="contained" 
            onClick={handleAddCamera}
            disabled={!newCourtCameraName || !newCourtCameraUrl}
            className="bg-gradient-to-r from-[#00a693] to-[#5a7684] hover:from-[#5a7684] hover:to-[#00a693] text-white rounded-lg px-6 py-2 font-semibold text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:from-gray-300 disabled:to-gray-400 disabled:transform-none disabled:shadow-none"
          >
            Add Camera
          </Button>
        </div>
      </div>

      {/* Camera List */}
      {Object.keys(cameraUrls || {}).length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <Typography variant="subtitle2" className="text-[#5a7684] font-semibold mb-3">Added Cameras:</Typography>
          <List className="space-y-2">
            {Object.entries(cameraUrls || {}).map(([name, url]) => (
              <ListItem key={name} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                <ListItemText
                  primary={<span className="font-semibold text-[#5a7684]">{name}</span>}
                  secondary={<span className="text-[#666666] font-mono text-sm">{url}</span>}
                />
                <div className="flex gap-2 items-center">
                  <IconButton 
                  size="small" 
                  onClick={() => handleRemoveCamera(name)}
                  aria-label={`Remove camera ${name}`}
                >
                  <FiX />
                  </IconButton>
                </div>
              </ListItem>
            ))}
          </List>
        </div>
      )}

      {/* Sessions Section */}
      <div className="mt-6">
        <Typography variant="subtitle1" className="text-[#5a7684] font-semibold mb-4">Add Court Sessions</Typography>
        <div className="flex flex-wrap gap-3 items-center">
          <TextField
            label="Session Name"
            value={tempSessionName}
            onChange={(e) => setTempSessionName(e.target.value)}
            placeholder="(Optional) e.g. Monday Morning"
            className="min-w-[200px] [&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-notchedOutline]:border-gray-300 hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
          />
          <FormControl className="min-w-[150px]">
          <InputLabel>Day of Week</InputLabel>
            <Select
              value={tempDayOfWeek}
              onChange={(e) => setTempDayOfWeek(e.target.value)}
              label="Day of Week"
              className="[&_.MuiOutlinedInput-notchedOutline]:border-gray-300 [&_.MuiOutlinedInput-notchedOutline]:rounded-lg hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
            >
              {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Start Time (HH:mm:ss)"
            value={tempStartTime}
            onChange={(e) => setTempStartTime(e.target.value)}
            className="min-w-[150px] [&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-notchedOutline]:border-gray-300 hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
          />
          <TextField
            label="End Time (HH:mm:ss)"
            value={tempEndTime}
            onChange={(e) => setTempEndTime(e.target.value)}
            className="min-w-[150px] [&_.MuiOutlinedInput-root]:rounded-lg [&_.MuiOutlinedInput-notchedOutline]:border-gray-300 hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
          />
          <FormControl className="min-w-[100px]">
            <InputLabel>Active?</InputLabel>
            <Select
              value={tempActive}
              onChange={(e) => setTempActive(e.target.value === 'true')}
              label="Active?"
              className="[&_.MuiOutlinedInput-notchedOutline]:border-gray-300 [&_.MuiOutlinedInput-notchedOutline]:rounded-lg hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            onClick={handleAddSession}
            className="bg-gradient-to-r from-[#00a693] to-[#5a7684] hover:from-[#5a7684] hover:to-[#00a693] text-white rounded-lg px-6 py-2 font-semibold text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
          >
            Add Session
          </Button>
        </div>
      </div>
      {/* Sessions List */}
      {sessions.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <Typography variant="subtitle2" className="text-[#5a7684] font-semibold mb-3">Current Sessions:</Typography>
          <div className="space-y-2">
            {sessions.map((s, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                <span className="text-[#5a7684] font-medium">
                  {s.sessionName} – {s.dayOfWeek}, {s.startTime}-{s.endTime}, Active={String(s.active)}
                </span>
                <Button 
                  color="error" 
                  onClick={() => handleRemoveSession(index)}
                  className="bg-gradient-to-r from-[#BD2026] to-[#ff6f3c] hover:from-[#ff6f3c] hover:to-[#BD2026] text-white rounded-lg px-4 py-1 font-semibold text-xs shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submit Button */}
      <Button 
        type="submit" 
        variant="contained" 
        className="bg-gradient-to-r from-[#BD2026] to-[#ff6f3c] hover:from-[#ff6f3c] hover:to-[#BD2026] text-white rounded-lg px-8 py-3 font-bold text-sm tracking-wide shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 mt-6"
      >
        Create Court
      </Button>
    </form>
  </div>
);

AddCourtSection.propTypes = {
  newCourtName: PropTypes.string.isRequired,
  setNewCourtName: PropTypes.func.isRequired,
  newCourtHlsUrl: PropTypes.string.isRequired,
  setNewCourtHlsUrl: PropTypes.func.isRequired,
  newCourtCameraName: PropTypes.string.isRequired,
  setNewCourtCameraName: PropTypes.func.isRequired,
  newCourtCameraUrl: PropTypes.string.isRequired,
  setNewCourtCameraUrl: PropTypes.func.isRequired,
  sessions: PropTypes.array.isRequired,
  tempSessionName: PropTypes.string.isRequired,
  setTempSessionName: PropTypes.func.isRequired,
  tempDayOfWeek: PropTypes.string.isRequired,
  setTempDayOfWeek: PropTypes.func.isRequired,
  tempStartTime: PropTypes.string.isRequired,
  setTempStartTime: PropTypes.func.isRequired,
  tempEndTime: PropTypes.string.isRequired,
  setTempEndTime: PropTypes.func.isRequired,
  tempActive: PropTypes.bool.isRequired,
  setTempActive: PropTypes.func.isRequired,
  handleAddSession: PropTypes.func.isRequired,
  handleRemoveSession: PropTypes.func.isRequired,
  handleAddCourt: PropTypes.func.isRequired,
  cameraUrls: PropTypes.object,
  handleAddCamera: PropTypes.func.isRequired,
  handleRemoveCamera: PropTypes.func.isRequired
};

AddCourtSection.defaultProps = {
  cameraUrls: {},
  // ... other default props if needed ...
};

export default AddCourtSection;
